/**
 * `agent-card-field-routing` — declarative per-field routing for the
 * dual-edit SuggestionsModal (ADR-0127 Slice 1).
 *
 * ADR-0118 mandated ONE storage home per field; ADR-0125 shipped the
 * per-field editable modal against `agent_card_profile`; ADR-0127 keeps
 * the storage-home invariant intact while extending the dashboard's
 * EDIT surface count from one to two. The mechanism is a single
 * declarative table mapping each editable `field_id` to:
 *
 *   - which backend route the modal's apply call posts to
 *     (`agent_card_profile` → eTag path; `site_compliance` → no eTag)
 *   - which validation kind to apply client-side
 *   - the byte cap the route accepts
 *
 * The shared modal does NOT know about routes. It looks up the route
 * from this table and dispatches to either `applyAgentCardProfile`
 * (the ADR-0125 path) or `updateSiteCompliance` (the new ADR-0127
 * path). A future ADR that re-homes a field is a one-line change here;
 * the modal does not change.
 *
 * The asymmetry between the two routes' concurrency models is
 * documented in ADR-0127 §4: `agent_card_profile` carries eTag because
 * it pre-dates partial-merge; `site_compliance` is partial-merge by
 * construction (route does its own `upsertSiteCompliance` merge), so
 * last-write-wins is the existing reality and this ADR does not
 * pre-emptively change it.
 *
 * Module boundary: internal to the dashboard app. The wire shapes
 * (agent-card-profile apply request, compliance PUT request) are owned
 * by their respective api clients; this module only routes between
 * them.
 */

/** Where a field's raw value lives on the server. Drives the apply
 *  dispatch in the SuggestionsModal. */
export type FieldStorageHome = 'agent_card_profile' | 'site_compliance';

/**
 * Closed enum of validation kinds the shared modal applies BEFORE
 * dispatching. The server validators on both routes remain the
 * authoritative floor — a value that passes the client check but
 * fails the server still 400s in the modal's existing error path.
 *
 * - `free_text`: non-empty when set, length-capped to `max_bytes`.
 *   Used by `description`, `price_range`, `shipping_zones`,
 *   `return_window_days`, `subscription_billing`, `returns_policy`.
 * - `url`: non-empty when set, must start with `https://`,
 *   length-capped. Used by `seller_tos_url`,
 *   `seller_privacy_policy_url`. Mirrors the server's
 *   `isValidComplianceUrl` predicate at
 *   `services/api/src/http/routes/v1/catalog.ts:166-177`.
 * - `enum_bool`: value MUST be `'true'` or `'false'`. Used by
 *   `agent_card_authoritative` (mirrors the server CHECK constraint).
 * - `currency`: short token (≤ 8 chars). Used by `currency`.
 */
export type FieldValidationKind =
  | 'free_text'
  | 'url'
  | 'enum_bool'
  | 'currency';

/**
 * The closed union of editable field IDs the SuggestionsModal can save.
 * Seven are owned by `agent_card_profile` (the existing ADR-0125
 * editable set); three are owned by `site_compliance` (the new
 * ADR-0127 editable set).
 *
 * `name` is intentionally NOT in this union — `name` is set during
 * onboarding via a separate route and is never edited through the
 * per-field modal.
 *
 * Catalog-derived aggregates (`currency` / `price_range` from
 * `catalog_product` — see ADR-0127 §1's field matrix) are NOT in this
 * union either; their edit surface is "upload a catalog or connect
 * Shopify," not a single-field modal save.
 */
export type AgentCardEditableFieldId =
  // agent_card_profile — the seven card fields from ADR-0125.
  | 'description'
  | 'price_range'
  | 'currency'
  | 'shipping_zones'
  | 'return_window_days'
  | 'subscription_billing'
  | 'agent_card_authoritative'
  // site_compliance — the three compliance fields ADR-0127 adds
  // to the Visibility view's inline editor.
  | 'returns_policy'
  | 'seller_tos_url'
  | 'seller_privacy_policy_url';

/**
 * Per-field route + validation descriptor. The `field_id` is kept on
 * the record so a TypeScript-narrowed map lookup carries the field
 * identity into the consumer without a second indirection.
 */
export interface FieldRoute {
  readonly field_id: AgentCardEditableFieldId;
  readonly storage_home: FieldStorageHome;
  readonly validation: FieldValidationKind;
  /**
   * Soft byte cap applied client-side. The server validators are the
   * authoritative floor; this cap exists so the modal can surface a
   * friendly "too long" message without round-tripping. Aligned with
   * the server's column CHECK constraints (compliance route + agent
   * card profile route).
   */
  readonly max_bytes: number;
}

/**
 * The single declarative routing table covering all 10 editable
 * fields. Built as a tuple-of-tuples first so the union exhaustiveness
 * is enforced at compile time: every `AgentCardEditableFieldId`
 * literal MUST appear exactly once in `FIELD_ROUTING_ENTRIES`.
 */
const FIELD_ROUTING_ENTRIES: ReadonlyArray<readonly [AgentCardEditableFieldId, FieldRoute]> = [
  [
    'description',
    {
      field_id: 'description',
      storage_home: 'agent_card_profile',
      validation: 'free_text',
      // Mirrors the description column CHECK (≤2048 chars) on
      // `agent_card_profile`.
      max_bytes: 2048,
    },
  ],
  [
    'price_range',
    {
      field_id: 'price_range',
      storage_home: 'agent_card_profile',
      validation: 'free_text',
      max_bytes: 64,
    },
  ],
  [
    'currency',
    {
      field_id: 'currency',
      storage_home: 'agent_card_profile',
      validation: 'currency',
      max_bytes: 8,
    },
  ],
  [
    'shipping_zones',
    {
      field_id: 'shipping_zones',
      storage_home: 'agent_card_profile',
      validation: 'free_text',
      max_bytes: 512,
    },
  ],
  [
    'return_window_days',
    {
      field_id: 'return_window_days',
      storage_home: 'agent_card_profile',
      validation: 'free_text',
      max_bytes: 32,
    },
  ],
  [
    'subscription_billing',
    {
      field_id: 'subscription_billing',
      storage_home: 'agent_card_profile',
      validation: 'free_text',
      max_bytes: 128,
    },
  ],
  [
    'agent_card_authoritative',
    {
      field_id: 'agent_card_authoritative',
      storage_home: 'agent_card_profile',
      validation: 'enum_bool',
      max_bytes: 5, // 'true' or 'false'
    },
  ],
  [
    'returns_policy',
    {
      field_id: 'returns_policy',
      storage_home: 'site_compliance',
      // Free-text policy per the compliance route's contract; the
      // route accepts any non-empty string or `null` to clear.
      validation: 'free_text',
      // The compliance route does not enforce a hard byte cap beyond
      // the column type; pick a generous floor that catches obvious
      // paste-the-whole-page mistakes without surprising merchants
      // with reasonable policy blocks.
      max_bytes: 8192,
    },
  ],
  [
    'seller_tos_url',
    {
      field_id: 'seller_tos_url',
      storage_home: 'site_compliance',
      validation: 'url',
      // URLs are practically capped well below this; chosen to align
      // with common browser / proxy limits.
      max_bytes: 2048,
    },
  ],
  [
    'seller_privacy_policy_url',
    {
      field_id: 'seller_privacy_policy_url',
      storage_home: 'site_compliance',
      validation: 'url',
      max_bytes: 2048,
    },
  ],
];

/**
 * Public lookup map keyed by `field_id`. Use {@link routeFor} for a
 * defensively typed lookup; the raw `Map` is exported for tests that
 * walk every entry to assert one-to-one coverage of the union.
 */
export const FIELD_ROUTING: ReadonlyMap<AgentCardEditableFieldId, FieldRoute> =
  new Map(FIELD_ROUTING_ENTRIES);

/**
 * Type-narrowed lookup. Returns `undefined` when the field is not in
 * the routing table — callers in TypeScript-strict code should treat
 * `undefined` as "field is read-only / not editable from this surface."
 */
export function routeFor(
  field_id: string,
): FieldRoute | undefined {
  return FIELD_ROUTING.get(field_id as AgentCardEditableFieldId);
}

/**
 * Validate a value against the field's `FieldValidationKind`. Returns
 * `null` on success or an error code string suitable for surfacing in
 * the modal's inline-error path. The server validators are the
 * authoritative floor; this check exists for friendly UX only.
 *
 * Error codes mirror the server's `reason` discriminators where
 * possible so a client failure and a server failure surface the same
 * copy to the merchant.
 */
export function validateFieldValue(
  field_id: AgentCardEditableFieldId,
  value: string,
): { ok: true } | { ok: false; reason: string } {
  const route = FIELD_ROUTING.get(field_id);
  if (route === undefined) {
    return { ok: false, reason: 'unknown_field' };
  }
  // An empty value is allowed at the modal boundary because the
  // server treats empty == clear on the compliance route, and the
  // agent_card_profile route handles its own empty=>NULL semantics.
  // The merchant's intent ("clear this field") is honored by the
  // backend; we only block obviously malformed non-empty values.
  if (value.length === 0) {
    return { ok: true };
  }
  if (value.length > route.max_bytes) {
    return { ok: false, reason: `${field_id}_too_long` };
  }
  switch (route.validation) {
    case 'free_text':
      // Non-empty already established above; nothing more to check.
      return { ok: true };
    case 'url': {
      if (!value.startsWith('https://')) {
        return { ok: false, reason: `invalid_${field_id}` };
      }
      try {
        const parsed = new URL(value);
        if (parsed.protocol !== 'https:') {
          return { ok: false, reason: `invalid_${field_id}` };
        }
      } catch {
        return { ok: false, reason: `invalid_${field_id}` };
      }
      return { ok: true };
    }
    case 'enum_bool':
      if (value !== 'true' && value !== 'false') {
        return {
          ok: false,
          reason: `invalid_${field_id}_value`,
        };
      }
      return { ok: true };
    case 'currency':
      // The server validator enforces the actual allow-list; we just
      // sanity-check the obvious shape so the merchant can't paste in
      // an entire sentence. A more permissive check than the server,
      // which is the right posture — the server is the floor.
      if (value.length > route.max_bytes) {
        return { ok: false, reason: `${field_id}_too_long` };
      }
      return { ok: true };
    default: {
      // Exhaustiveness guard — narrows `route.validation` to `never`
      // when every kind above is handled.
      const _exhaustive: never = route.validation;
      void _exhaustive;
      return { ok: true };
    }
  }
}
